import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import { useImpresion } from '../../contexts/ContextoImpresion';
import qzTrayService from '../../services/qzTrayService';
import SpinnerCargando from '../util/SpinnerCargando';
import { FaPrint, FaSync, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const OPCIONES_ANCHO = [
  { value: '80mm', label: '80mm (estándar)' },
  { value: '58mm', label: '58mm (compacta)' },
];

function ConfiguracionImpresionCaja() {
  const [cajas, setCajas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editandoId, setEditandoId] = useState(null);
  const [formEdit, setFormEdit] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [probandoId, setProbandoId] = useState(null);

  const { qzConectado, impresoras, refrescarImpresoras } = useImpresion();

  const cargarCajas = useCallback(async () => {
    try {
      setCargando(true);
      const resp = await instanciaAxios.get('/cuenta_tesoreria/cajas-impresion');
      setCajas(resp.data?.data || []);
    } catch (err) {
      toast.error('Error al cargar las cajas');
      console.error(err);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargarCajas();
  }, [cargarCajas]);

  const iniciarEdicion = (caja) => {
    setEditandoId(caja.id_cuenta_tesoreria);
    setFormEdit({
      nombre_impresora_termica: caja.nombre_impresora_termica || '',
      ancho_papel_termica: caja.ancho_papel_termica || '80mm',
    });
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setFormEdit({});
  };

  const guardarEdicion = async (idCaja) => {
    try {
      setGuardando(true);
      await instanciaAxios.put(`/cuenta_tesoreria/${idCaja}/impresion`, formEdit);
      toast.success('Configuración guardada');
      setEditandoId(null);
      setFormEdit({});
      await cargarCajas();
    } catch (err) {
      toast.error('Error al guardar la configuración');
      console.error(err);
    } finally {
      setGuardando(false);
    }
  };

  const probarImpresora = async (nombreImpresora, idCaja) => {
    if (!nombreImpresora) {
      toast.warning('Seleccione una impresora primero');
      return;
    }
    setProbandoId(idCaja);
    try {
      const ok = await qzTrayService.imprimirPrueba(nombreImpresora);
      if (ok) toast.success('Impresión de prueba enviada');
      else toast.warning('No se pudo imprimir la prueba');
    } catch {
      toast.error('Error al probar la impresora');
    } finally {
      setProbandoId(null);
    }
  };

  const handleRefrescar = async () => {
    await refrescarImpresoras();
    toast.info('Lista de impresoras actualizada');
  };

  if (cargando) return <SpinnerCargando />;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Configuración de Impresión por Caja</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure la impresora térmica y tamaño de papel para cada caja registradora.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {qzConectado ? (
            <span className="flex items-center gap-1.5 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
              <FaCheckCircle /> QZ Tray conectado
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-sm text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full">
              <FaTimesCircle /> QZ Tray no detectado
            </span>
          )}
          {qzConectado && (
            <button
              onClick={handleRefrescar}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors"
            >
              <FaSync size={12} /> Refrescar impresoras
            </button>
          )}
        </div>
      </div>

      {cajas.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-500">
          No hay cajas con sesión habilitada configuradas.
        </div>
      ) : (
        <div className="space-y-4">
          {cajas.map((caja) => {
            const esEdicion = editandoId === caja.id_cuenta_tesoreria;
            const impresora = esEdicion ? formEdit.nombre_impresora_termica : (caja.nombre_impresora_termica || '');
            const ancho = esEdicion ? formEdit.ancho_papel_termica : (caja.ancho_papel_termica || '80mm');

            return (
              <div
                key={caja.id_cuenta_tesoreria}
                className={`bg-white border rounded-xl p-5 shadow-sm transition-all ${
                  esEdicion ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {caja.descripcion}
                  </h3>
                  {!esEdicion ? (
                    <button
                      onClick={() => iniciarEdicion(caja)}
                      className="px-4 py-1.5 bg-color-primario text-white rounded-lg hover:opacity-90 text-sm transition-colors"
                    >
                      Editar
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => guardarEdicion(caja.id_cuenta_tesoreria)}
                        disabled={guardando}
                        className="px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm transition-colors"
                      >
                        {guardando ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button
                        onClick={cancelarEdicion}
                        disabled={guardando}
                        className="px-4 py-1.5 bg-gray-400 text-white rounded-lg hover:bg-gray-500 text-sm transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Printer selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Impresora Térmica
                    </label>
                    {esEdicion ? (
                      <div className="flex gap-2 items-center">
                        {qzConectado ? (
                          <select
                            value={formEdit.nombre_impresora_termica || ''}
                            onChange={(e) => setFormEdit(prev => ({ ...prev, nombre_impresora_termica: e.target.value }))}
                            className="flex-1 p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                          >
                            <option value="">Sin impresora</option>
                            {impresoras.map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={formEdit.nombre_impresora_termica || ''}
                            onChange={(e) => setFormEdit(prev => ({ ...prev, nombre_impresora_termica: e.target.value }))}
                            placeholder="Nombre de la impresora"
                            className="flex-1 p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 outline-none transition-all"
                          />
                        )}
                        {qzConectado && (
                          <button
                            onClick={() => probarImpresora(formEdit.nombre_impresora_termica, caja.id_cuenta_tesoreria)}
                            disabled={!formEdit.nombre_impresora_termica || probandoId === caja.id_cuenta_tesoreria}
                            className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm transition-colors whitespace-nowrap"
                          >
                            <FaPrint size={12} />
                            {probandoId === caja.id_cuenta_tesoreria ? 'Probando...' : 'Probar'}
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-800 p-2">
                        {impresora || <span className="text-gray-400 italic">Sin configurar</span>}
                      </p>
                    )}
                  </div>

                  {/* Paper width selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Ancho del Papel
                    </label>
                    {esEdicion ? (
                      <select
                        value={formEdit.ancho_papel_termica || '80mm'}
                        onChange={(e) => setFormEdit(prev => ({ ...prev, ancho_papel_termica: e.target.value }))}
                        className="w-full p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                      >
                        {OPCIONES_ANCHO.map((op) => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-gray-800 p-2">
                        {OPCIONES_ANCHO.find(o => o.value === ancho)?.label || ancho}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ConfiguracionImpresionCaja;
